import React, { useMemo, useState } from "react";
import { withTranslation } from "react-i18next";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Grid,
} from "@mui/material";
import Typography from "@mui/material/Typography";
import PropTypes from "prop-types";
import { useFormik } from "formik";
import { isEmpty } from "lodash";
import moment from "moment";
import * as Yup from "yup";

import PaymentRequest from "Components/PaymentRequest";
import { getOptionLabel, getOptionValue } from "common/enums";
import DatePicker from "Components/Common/DatePicker";
import Modal from "Components/Common/Modal";
import InputFieldText from "Components/Common/InputFieldText";

import { usePostElectricityBillPaymentMutation } from "apis/Billing";

import { convertUTCDateToLocalDate } from "utils/convertUTCDateToLocalDate";
import { filterEmptyStrings, onlyNumberKeys } from "utils";
import { dateFormatter } from "utils/dateFormatter";

import { usePostSecurityDepositBillPaymentMutation } from "apis/SecurityDeposit";
import { usePostBackupPowerBillPaymentMutation } from "apis/BackupPower";
import { usePostDisconnectedConsumerPaymentMutation } from "apis/UtilityConsumer/utilityConsumer";

import { BillingCalculationCard } from "./style";

const DashboardItem = ({
  item: {
    id,
    consumerNumber,
    billCycle,
    currentMonthBill,
    pendingDues,
    netPayable,
    igst,
    cgst,
    sgst,
    paymentDueDate,
    utilityConsumerStatus,
    status,
    reconnectionCharges,
    billCalculations,
    payableAmount: currentPayableAmount,
    tariffId,
  },
  billType,
  isOtherUtility,
  t,
}) => {
  const [getEncryptionForPaymentForDisconnectedConsumerPayment, { isLoading }] =
    usePostDisconnectedConsumerPaymentMutation();
  const [
    getEncryptionForPaymentForElectricity,
    { isLoading: isElectricityBillPaying },
  ] = usePostElectricityBillPaymentMutation();
  const [
    getEncryptionForPaymentForSecurityDeposit,
    { isLoading: isSecurityDepositBillPaying },
  ] = usePostSecurityDepositBillPaymentMutation();
  const [
    getEncryptionForPaymentForBackupPower,
    { isLoading: isBackupPowerBillPaying },
  ] = usePostBackupPowerBillPaymentMutation();

  const [isAmountModalOpen, setIsAmountModalOpen] = useState(false);

  const formik = useFormik({
    initialValues: {
      year: moment(),
      payableAmount: netPayable + (reconnectionCharges ?? 0) ?? "",
      amount: currentPayableAmount + (reconnectionCharges ?? 0),
      tdsAmount: 0,
      netPayable: netPayable + (reconnectionCharges ?? 0) ?? 0,
      cgstAmount: cgst ?? 0,
      igstAmount: igst ?? 0,
      sgstAmount: sgst ?? 0,
      cgstTdsAmount: 0,
      cgstTdsYear: moment(),
      igstTdsAmount: 0,
      igstTdsYear: moment(),
      sgstTdsAmount: 0,
      sgstTdsYear: moment(),
      isTds: false,
    },
    validationSchema: Yup.object({
      amount: Yup.number()
        .required("Amount is required")
        .test("isAmountRequired", "", function () {
          const [{ value: current = {} } = {}] = this.from || [];
          if (!isOtherUtility && !current.amount) {
            return this.createError({
              message: "Amount must be greater than 0",
            });
          }
          return true;
        }),
      year: Yup.date().test("isValidYear", "", function () {
        const [{ value: current = {} } = {}] = this.from || [];
        if (!current.year && current.isTds) {
          return this.createError({
            message: "Select financial year",
          });
        }

        return true;
      }),
      ...(isOtherUtility && {
        ...(!!cgst && {
          cgstAmount: Yup.number().required("CGST amount is required"),
          cgstTdsAmount: Yup.number()
            .required("CGST TDS amount is required")
            .test("isValidCgstTDSAmount", "", function () {
              const [{ value: current = null } = {}] = this.from || [];
              if (current.cgstTdsAmount > current.cgstAmount && current.isTds) {
                return this.createError({
                  message: "CGST TDS amount must be less than CGST amount",
                });
              }
              return true;
            }),
        }),
        ...(!!igst && {
          igstAmount: Yup.number().required("IGST amount is required"),
          igstTdsAmount: Yup.number()
            .required("IGST TDS amount is required")
            .test("isValidIgstTDSAmount", "", function () {
              const [{ value: current = null } = {}] = this.from || [];
              if (current.igstTdsAmount > current.igstAmount && current.isTds) {
                return this.createError({
                  message: "IGST TDS amount must be less than IGST amount",
                });
              }

              return true;
            }),
        }),
        ...(!!sgst && {
          sgstAmount: Yup.number().required("SGST amount is required"),
          sgstTdsAmount: Yup.number()
            .required("SGST TDS amount is required")
            .test("isValidSgstTDSAmount", "", function () {
              const [{ value: current = null } = {}] = this.from || [];
              if (current.sgstTdsAmount > current.sgstAmount && current.isTds) {
                return this.createError({
                  message: "SGST TDS amount must be less than SGST amount",
                });
              }
              return true;
            }),
        }),
      }),
      tdsAmount: Yup.number()
        .when("isTds", {
          is: true,
          then: Yup.number().required("TDS amount is required"),
        })
        .test("isValidTDSAmount", "", function () {
          const [{ value: current = null } = {}] = this.from || [];
          if (current.tdsAmount > current.amount && current.isTds) {
            return this.createError({
              message: "TDS amount must be less than amount",
            });
          }

          return true;
        }),
    }),
  });

  const {
    setFieldValue,
    errors,
    values: {
      amount,
      tdsAmount,
      isTds,
      year,
      cgstAmount,
      igstAmount,
      sgstAmount,
      cgstTdsAmount,
      igstTdsAmount,
      sgstTdsAmount,
      igstTdsYear,
      cgstTdsYear,
      sgstTdsYear,
    },
  } = formik;

  const payNowBtnHandler = async (id) => {
    if (isEmpty(errors)) {
      try {
        if (
          getOptionValue("subscriptionTypes", "Electricity") === billType &&
          utilityConsumerStatus ===
            getOptionValue(
              "utilityConsumerStatusEnum",
              "Temporary Disconnected"
            )
        ) {
          return await getEncryptionForPaymentForDisconnectedConsumerPayment(
            id
          );
        }
        if (getOptionValue("subscriptionTypes", "Electricity") === billType) {
          const payload = filterEmptyStrings({
            amount,
            tdsAmount,
            tdsYear: isTds ? year.format("YYYY") : "",
            ...(isOtherUtility && {
              cgstAmount,
              igstAmount,
              sgstAmount,
              cgstTdsAmount: cgstTdsAmount || 0,
              cgstTdsYear: cgstTdsYear ? cgstTdsYear.format("YYYY") : "",
              igstTdsAmount: igstTdsAmount || 0,
              igstTdsYear: igstTdsYear ? igstTdsYear.format("YYYY") : "",
              sgstTdsAmount: sgstTdsAmount || 0,
              sgstTdsYear: sgstTdsYear ? sgstTdsYear.format("YYYY") : "",
            }),
          });
          return await getEncryptionForPaymentForElectricity({
            billCalculationIds: id,
            ...payload,
          });
        }
        if (
          getOptionValue("subscriptionTypes", "Security Deposit") === billType
        ) {
          return await getEncryptionForPaymentForSecurityDeposit(
            id,
            filterEmptyStrings({
              amount,
              tdsYear: isTds ? year.format("YYYY") : "",
              tdsAmount,
            })
          );
        }
        return await getEncryptionForPaymentForBackupPower(
          id,
          filterEmptyStrings({
            amount,
            tdsYear: isTds ? year.format("YYYY") : "",
            tdsAmount,
          })
        );
      } catch (error) {
        console.error(error);
      }
    }
  };

  const payableAmount = useMemo(
    () => netPayable + (reconnectionCharges ?? 0),
    [billCalculations, reconnectionCharges, netPayable]
  );

  return (
    <>
      {isAmountModalOpen && (
        <Modal
          isOpen
          handleClose={() => setIsAmountModalOpen(false)}
          maxWidth={isOtherUtility ? "md" : "sm"}
          title="Bill Payment"
        >
          <Grid container columnSpacing={2} className="mt-3">
            <Grid item xs={12}>
              <InputFieldText
                label="Net Payable*"
                type="number"
                InputProps={{
                  inputProps: {
                    min: 0,
                    onKeyDown: (e) => onlyNumberKeys(e, true),
                  },
                }}
                formik={formik}
                disabled
                name="payableAmount"
              />
            </Grid>
            <Grid item xs={isOtherUtility ? 6 : 12}>
              <InputFieldText
                label="Amount*"
                type="number"
                InputProps={{
                  inputProps: {
                    min: 0,
                    onKeyDown: (e) => onlyNumberKeys(e, true),
                  },
                }}
                formik={formik}
                name="amount"
                placeholder="Enter Amount"
              />
            </Grid>
            {isOtherUtility && (
              <>
                {!!cgst && (
                  <Grid item xs={isOtherUtility ? 6 : 12}>
                    <InputFieldText
                      label="CGST Amount*"
                      type="number"
                      InputProps={{
                        inputProps: {
                          min: 0,
                          onKeyDown: (e) => onlyNumberKeys(e, true),
                        },
                      }}
                      formik={formik}
                      name="cgstAmount"
                      placeholder="Enter CGST Amount"
                    />
                  </Grid>
                )}
                {!!igst && (
                  <Grid item xs={isOtherUtility ? 6 : 12}>
                    <InputFieldText
                      label="IGST Amount*"
                      type="number"
                      InputProps={{
                        inputProps: {
                          min: 0,
                          onKeyDown: (e) => onlyNumberKeys(e, true),
                        },
                      }}
                      formik={formik}
                      name="igstAmount"
                      placeholder="Enter IGST Amount"
                    />
                  </Grid>
                )}
                {!!sgst && (
                  <Grid item xs={isOtherUtility ? 6 : 12}>
                    <InputFieldText
                      label="SGST Amount*"
                      type="number"
                      InputProps={{
                        inputProps: {
                          min: 0,
                          onKeyDown: (e) => onlyNumberKeys(e, true),
                        },
                      }}
                      formik={formik}
                      name="sgstAmount"
                      placeholder="Enter SGST Amount"
                    />
                  </Grid>
                )}
              </>
            )}

            {tariffId !== getOptionValue("tariff", "RGP") && (
              <>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={!!isTds}
                        onChange={() => {
                          setFieldValue("isTds", !isTds);
                          setFieldValue("tdsAmount", 0);
                        }}
                      />
                    }
                    label={t("TDS")}
                  />
                </Grid>
                {isTds && (
                  <>
                    <Grid item xs={12} sm={6}>
                      <DatePicker
                        slotProps={{
                          textField: {
                            size: "small",
                          },
                        }}
                        placeholder="Financial Year"
                        name="year"
                        formik={formik}
                        label={t("Financial Year*")}
                        className="w-100"
                        views={["year"]}
                        format={dateFormatter(year)}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <InputFieldText
                        label="TDS Amount*"
                        type="number"
                        InputProps={{
                          inputProps: {
                            min: 0,
                            onKeyDown: (e) => onlyNumberKeys(e, true),
                          },
                        }}
                        formik={formik}
                        name="tdsAmount"
                        placeholder="Enter TDS Amount"
                      />
                    </Grid>

                    {isOtherUtility && (
                      <>
                        {!!cgst && (
                          <>
                            <Grid item xs={12} sm={6}>
                              <DatePicker
                                slotProps={{
                                  textField: {
                                    size: "small",
                                  },
                                }}
                                placeholder="CGST Financial Year"
                                name="cgstTdsYear"
                                formik={formik}
                                label={t("CGST Financial Year*")}
                                className="w-100"
                                views={["year"]}
                                format={dateFormatter(cgstTdsYear)}
                              />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <InputFieldText
                                label="CGST TDS Amount*"
                                type="number"
                                InputProps={{
                                  inputProps: {
                                    min: 0,
                                    onKeyDown: (e) => onlyNumberKeys(e, true),
                                  },
                                }}
                                formik={formik}
                                name="cgstTdsAmount"
                                placeholder="Enter CGST TDS Amount"
                              />
                            </Grid>
                          </>
                        )}
                        {!!igst && (
                          <>
                            <Grid item xs={12} sm={6}>
                              <DatePicker
                                slotProps={{
                                  textField: {
                                    size: "small",
                                  },
                                }}
                                placeholder="IGST Financial Year"
                                name="igstTdsYear"
                                formik={formik}
                                label={t("IGST Financial Year*")}
                                className="w-100"
                                views={["year"]}
                                format={dateFormatter(igstTdsYear)}
                              />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <InputFieldText
                                label="IGST TDS Amount*"
                                type="number"
                                InputProps={{
                                  inputProps: {
                                    min: 0,
                                    onKeyDown: (e) => onlyNumberKeys(e, true),
                                  },
                                }}
                                formik={formik}
                                name="igstTdsAmount"
                                placeholder="Enter IGST TDS Amount"
                              />
                            </Grid>
                          </>
                        )}
                        {!!sgst && (
                          <>
                            <Grid item xs={12} sm={6}>
                              <DatePicker
                                slotProps={{
                                  textField: {
                                    size: "small",
                                  },
                                }}
                                placeholder="SGST Financial Year"
                                name="sgstTdsYear"
                                formik={formik}
                                label={t("SGST Financial Year*")}
                                className="w-100"
                                views={["year"]}
                                format={dateFormatter(sgstTdsYear)}
                              />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <InputFieldText
                                label="SGST TDS Amount*"
                                type="number"
                                InputProps={{
                                  inputProps: {
                                    min: 0,
                                    onKeyDown: (e) => onlyNumberKeys(e, true),
                                  },
                                }}
                                formik={formik}
                                name="sgstTdsAmount"
                                placeholder="Enter SGST TDS Amount"
                              />
                            </Grid>
                          </>
                        )}
                      </>
                    )}
                  </>
                )}
              </>
            )}
            <Grid item xs={12} className="text-end mt-3">
              <PaymentRequest
                generateEncryption={() => payNowBtnHandler(id)}
                handleClose={() => setIsAmountModalOpen(false)}
                amount={amount}
              >
                <Button
                  color="primary"
                  className="mb-1 me-1"
                  variant="contained"
                  disabled={
                    isElectricityBillPaying ||
                    isBackupPowerBillPaying ||
                    isSecurityDepositBillPaying ||
                    isLoading
                  }
                >
                  {t("Pay Now")}
                  {(isElectricityBillPaying ||
                    isBackupPowerBillPaying ||
                    isSecurityDepositBillPaying ||
                    isLoading) && <CircularProgress size={20} />}
                </Button>
              </PaymentRequest>
            </Grid>
          </Grid>
        </Modal>
      )}
      <Grid item xs={12} sm={4} md={6}>
        <BillingCalculationCard>
          <Grid container spacing={2}>
            <Grid item xs={5}>
              <Typography className="fw-semibold">
                {t("Consumer Number")}:
              </Typography>
            </Grid>
            <Grid item xs={7}>
              <Typography>{consumerNumber}</Typography>
            </Grid>
            <Grid item xs={5}>
              <Typography className="fw-semibold">
                {t("Billing Cycle")}:
              </Typography>
            </Grid>
            <Grid item xs={7}>
              <Typography>{billCycle}</Typography>
            </Grid>
            <Grid item xs={5}>
              <Typography className="fw-semibold">
                {t("Current Month Bill")}:
              </Typography>
            </Grid>
            <Grid item xs={7}>
              <Typography>
                &#8377;
                {currentMonthBill}
              </Typography>
            </Grid>
            <Grid item xs={5}>
              <Typography className="fw-semibold">
                {t("Previous Dues")}:
              </Typography>
            </Grid>
            <Grid item xs={7}>
              <Typography>
                &#8377;
                {pendingDues}
              </Typography>
            </Grid>
            <Grid item xs={5}>
              <Typography className="fw-semibold">
                {t("Net Payable")}:
              </Typography>
            </Grid>
            <Grid item xs={7}>
              <Typography>
                &#8377;
                {payableAmount}
              </Typography>
            </Grid>
            <Grid item xs={5}>
              <Typography className="fw-semibold">
                {t("Payment Due Date")}:
              </Typography>
            </Grid>
            <Grid item xs={7}>
              <Typography>
                {convertUTCDateToLocalDate(paymentDueDate).format("DD-MM-YYYY")}
              </Typography>
            </Grid>
            <Grid item xs={5}>
              <Typography className="fw-semibold">{t("Status")}:</Typography>
            </Grid>
            <Grid item xs={7}>
              <Typography>
                {getOptionLabel("billCalculationStatus", status)}
              </Typography>
            </Grid>
            <Grid item xs={5}>
              <Typography className="fw-semibold">Consumer Status:</Typography>
            </Grid>
            <Grid item xs={7}>
              <Typography>
                {getOptionLabel(
                  "utilityConsumerStatusEnum",
                  utilityConsumerStatus
                )}
              </Typography>
            </Grid>
          </Grid>
          <Box className="d-flex justify-content-end mt-2">
            {getOptionValue("subscriptionTypes", "Electricity") === billType &&
            utilityConsumerStatus ===
              getOptionValue(
                "utilityConsumerStatusEnum",
                "Temporary Disconnected"
              ) ? (
              <PaymentRequest generateEncryption={() => payNowBtnHandler(id)}>
                <Button
                  variant="contained"
                  className="d-flex gap-2"
                  size="small"
                  disabled={
                    payableAmount <= 0 ||
                    getOptionValue("billCalculationStatus", "Paid") ===
                      status ||
                    isElectricityBillPaying ||
                    isBackupPowerBillPaying ||
                    isSecurityDepositBillPaying ||
                    isLoading
                  }
                >
                  {t("Pay Now")}
                  {(isElectricityBillPaying ||
                    isBackupPowerBillPaying ||
                    isSecurityDepositBillPaying ||
                    isLoading) && <CircularProgress size={20} />}
                </Button>
              </PaymentRequest>
            ) : (
              <Button
                variant="contained"
                className="d-flex gap-2"
                size="small"
                disabled={payableAmount <= 0}
                onClick={() => setIsAmountModalOpen(true)}
              >
                {t("Pay Now")}
              </Button>
            )}
          </Box>
        </BillingCalculationCard>
      </Grid>
    </>
  );
};

DashboardItem.propTypes = {
  item: PropTypes.object,
  t: PropTypes.func,
  billType: PropTypes.number,
  isOtherUtility: PropTypes.bool,
};

export default withTranslation()(DashboardItem);
